import React, { useState, useEffect, useCallback } from 'react';
import { Search, Plus, Pencil, Trash2, ChevronLeft, ChevronRight, Loader2, AlertTriangle } from 'lucide-react';
import api from '../../utils/api';
import ImageUpload from './ImageUpload';
import FileUpload from './FileUpload';

const AdminCRUD = ({ endpoint, title, icon: Icon, columns, formFields, defaultForm = {}, extraActions }) => {
  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [page, setPage]         = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm]         = useState(defaultForm);
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/${endpoint}/admin/all?page=${page}&limit=15${search ? `&search=${search}` : ''}`);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [endpoint, page, search]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openCreate = () => { setEditItem(null); setForm(defaultForm); setMsg(''); setShowModal(true); };
  const openEdit   = (item) => { setEditItem(item); setForm({ ...defaultForm, ...item }); setMsg(''); setShowModal(true); };
  const setField   = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setMsg('');
    try {
      if (editItem) await api.put(`/${endpoint}/${editItem._id}`, form);
      else          await api.post(`/${endpoint}`, form);
      setMsg('success');
      fetchItems();
      setTimeout(() => { setShowModal(false); setMsg(''); }, 900);
    } catch (err) { setMsg('error:' + (err.response?.data?.message || 'Save failed')); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/${endpoint}/${id}`); fetchItems(); }
    catch { alert('Delete failed'); }
    finally { setDeleteConfirm(null); }
  };

  const renderField = (field) => {
    const val = form[field.key];
    if (field.type === 'image') return <ImageUpload label={null} value={val || ''} onChange={url => setField(field.key, url)} />;
    if (field.type === 'file')  return <FileUpload  label={null} value={val || ''} onChange={url => setField(field.key, url)} accept={field.accept} />;
    if (field.type === 'select') return (
      <select value={val || ''} onChange={e => setField(field.key, e.target.value)} required={field.required}
        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-royal-800/20 focus:border-royal-800 transition-all bg-white">
        <option value="">Select {field.label}...</option>
        {field.options.map(o => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
      </select>
    );
    if (field.type === 'textarea') return (
      <textarea value={val || ''} onChange={e => setField(field.key, e.target.value)} rows={field.rows || 4}
        placeholder={field.placeholder} required={field.required}
        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-royal-800/20 focus:border-royal-800 transition-all resize-none" />
    );
    if (field.type === 'checkbox') return (
      <label className="flex items-center gap-2.5 cursor-pointer">
        <input type="checkbox" checked={!!val} onChange={e => setField(field.key, e.target.checked)}
          className="w-4 h-4 rounded border-gray-300 accent-royal-800 cursor-pointer" />
        <span className="text-sm text-gray-600">{field.checkLabel || 'Yes'}</span>
      </label>
    );
    return (
      <input type={field.type || 'text'} value={val || ''} onChange={e => setField(field.key, e.target.value)}
        placeholder={field.placeholder} required={field.required}
        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-royal-800/20 focus:border-royal-800 transition-all bg-white" />
    );
  };

  const isWide = (f) => f.fullWidth || ['image','file','textarea'].includes(f.type);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900 flex items-center gap-2">
            {Icon && <Icon size={22} className="text-royal-800" />} {title}
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">{total} total records</p>
        </div>
        <button onClick={openCreate} className="btn-royal text-sm py-2.5 px-5">
          <Plus size={16} /> Add New
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-card border border-gray-100 overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-3.5 border-b border-gray-100">
          <div className="relative flex-1 max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-royal-800/20 focus:bg-white transition-all"
              placeholder={`Search ${title.toLowerCase()}...`}
              value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
          </div>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center h-48 gap-2 text-gray-400">
              <Loader2 size={20} className="animate-spin" /> Loading...
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-gray-400 gap-2">
              {Icon && <Icon size={32} className="opacity-30" />}
              <p className="text-sm">No {title.toLowerCase()} found</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  {columns.map(c => <th key={c.key} className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wide px-5 py-3">{c.label}</th>)}
                  <th className="text-right text-xs font-semibold text-gray-400 uppercase tracking-wide px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {items.map(item => (
                  <tr key={item._id} className="hover:bg-gray-50/70 transition-colors">
                    {columns.map(c => <td key={c.key} className="px-5 py-3 text-gray-700">{c.render ? c.render(item[c.key], item) : (item[c.key] ?? '—')}</td>)}
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-1.5 justify-end">
                        {extraActions && extraActions(item, fetchItems)}
                        <button onClick={() => openEdit(item)} className="p-2 text-gray-400 hover:text-royal-800 hover:bg-royal-50 rounded-lg transition-colors" title="Edit">
                          <Pencil size={14} />
                        </button>
                        <button onClick={() => setDeleteConfirm(item._id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 text-xs text-gray-500">
          <span>{items.length} of {total}</span>
          <div className="flex items-center gap-2">
            <button disabled={page <= 1} onClick={() => setPage(p => p-1)}
              className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <ChevronLeft size={14} />
            </button>
            <span className="px-2">Page {page} of {Math.max(1, Math.ceil(total / 15))}</span>
            <button disabled={items.length < 15} onClick={() => setPage(p => p+1)}
              className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h3 className="font-display font-semibold text-gray-900">{editItem ? `Edit ${title.slice(0,-1)||title}` : `New ${title.slice(0,-1)||title}`}</h3>
              <button onClick={() => setShowModal(false)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 transition-colors">✕</button>
            </div>

            {msg.startsWith('error:') && (
              <div className="mx-6 mt-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">
                <AlertTriangle size={15} /> {msg.slice(6)}
              </div>
            )}
            {msg === 'success' && (
              <div className="mx-6 mt-4 bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm rounded-xl px-4 py-3">✅ Saved successfully</div>
            )}

            <form onSubmit={handleSubmit} className="p-6">
              <div className="grid grid-cols-2 gap-4">
                {formFields.map(field => (
                  <div key={field.key} className={isWide(field) ? 'col-span-2' : 'col-span-1 sm:col-span-1'}>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                      {field.label}{field.required && <span className="text-red-400 ml-0.5">*</span>}
                    </label>
                    {renderField(field)}
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-end gap-3 mt-6 pt-5 border-t border-gray-100">
                <button type="button" onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors">Cancel</button>
                <button type="submit" disabled={saving}
                  className="px-5 py-2 bg-royal-800 hover:bg-royal-900 text-white rounded-xl text-sm font-semibold transition-colors flex items-center gap-2 disabled:opacity-60">
                  {saving ? <><Loader2 size={14} className="animate-spin" /> Saving...</> : editItem ? '💾 Update' : '+ Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 size={22} className="text-red-600" />
            </div>
            <h3 className="font-display font-semibold text-gray-900 mb-2">Delete this item?</h3>
            <p className="text-gray-500 text-sm mb-6">This action cannot be undone. The item and any associated files will be permanently removed.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors">Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-semibold transition-colors">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default AdminCRUD;
